// Office review (M6, office-review prototype): the shapes the review screen reads, and the small
// rules it shows before the server enforces them (splits add to 100, who may reopen or void).

import { dateText } from '@flowology/kernel';
import type { MemberPlan } from '@flowology/domain';
import type { LicenceGap } from '../settings/licences-model.ts';
import type { VisitConfirmation } from '../job/confirmation-model.ts';
import type { CampaignRef } from '../settings/campaigns-model.ts';
import type { VideoUpload } from '../shared/gallery-model.ts';
import type { Display } from '../shared/photo-display-model.ts';
import type { AcceptanceSignature } from '../shared/signed-model.ts';
import type { AddressPointView } from '@flowology/ui-web';

export interface QueueJob {
  jobId: string;
  number: string;
  customerName: string;
  summary: string;
  sentBack: boolean;
  sentBackAt: string | null;
  sentBackReason: string | null;
  blocking: string[];
}

/** Where a page of jobs.reviewQueue ends; pass it back as `after` for the page that follows. */
export interface QueueAt {
  at: string;
  id: string;
}

/** jobs.reviewQueue's answer: a page of the queue, how many wait in all, and where the next page starts. */
export interface QueuePage {
  jobs: QueueJob[];
  total: number;
  next: QueueAt | null;
}

/** How many jobs the review screen reads at a time: each comes with its checklist, so pages stay small. */
export const QUEUE_PAGE = 50;

/**
 * The next page of the queue added to what's shown. A job already shown comes once: it can move
 * further back in the queue (touched) while the pages are read.
 */
export function addQueuePage(shown: QueuePage, page: QueuePage): QueuePage {
  const had = new Set(shown.jobs.map((j) => j.jobId));
  return {
    jobs: [...shown.jobs, ...page.jobs.filter((j) => !had.has(j.jobId))],
    total: page.total,
    next: page.next,
  };
}

/** How many pages to read again on a refresh, so the jobs "Load more" opened stay open. */
export const queuePages = (shown: number): number => Math.max(1, Math.ceil(shown / QUEUE_PAGE));

/** A job in Completed recently: its card's words. */
export interface DoneJob {
  id: string;
  number: string;
  customerName: string;
  summary: string;
}

/** Completed recently as read so far: the jobs, how many are completed in all, and whether more wait. */
export interface DonePage {
  jobs: DoneJob[];
  total: number;
  more: boolean;
}

/** How many completed jobs Review reads at a time; the rest come on "Load more" (list caps, BL-110). */
export const DONE_PAGE = 12;

/**
 * views.run's input for the jobs completed lately, the latest completed first: `limit` of them after the
 * `offset` already shown. A view, so the page has its total and the rest can be read on (jobs.list has no
 * cursor yet).
 */
export const doneInput = (offset = 0, limit = DONE_PAGE): Record<string, unknown> => ({
  spec: {
    entity: 'jobs',
    filter: { field: 'status', op: 'eq', value: 'completed' },
    columns: ['number', 'customer', 'summary'],
    sort: [{ field: 'completedAt', dir: 'desc' }],
    limit,
  },
  ...(offset ? { offset } : {}),
});

/** On a refresh, as many as were shown are read again: at least a page, and views.run's 500 at most. */
export const doneRereadLimit = (shown: number): number => Math.min(500, Math.max(DONE_PAGE, shown));

/** views.run's answer as Completed recently's page. */
export function donePage(r: {
  rows: Record<string, unknown>[];
  truncated: boolean;
  total?: number;
}): DonePage {
  const jobs = r.rows.map((row) => ({
    id: String(row['id']),
    number: String(row['number'] ?? ''),
    customerName: String(row['customer'] ?? ''),
    summary: String(row['summary'] ?? ''),
  }));
  return { jobs, total: r.total ?? jobs.length, more: r.truncated };
}

/** The next page under the jobs shown, each once (one completed between reads moves the rest down one). */
export function addDonePage(had: DonePage, page: DonePage): DonePage {
  const seen = new Set(had.jobs.map((j) => j.id));
  return {
    jobs: [...had.jobs, ...page.jobs.filter((j) => !seen.has(j.id))],
    total: page.total,
    more: page.more,
  };
}

export interface ChecklistItem {
  /**
   * overpaid: more paid than the invoice (QA-186); overrides: lines off the price list (F-21), both
   * heads-ups; po: the customer requires a PO and the invoice has none, which blocks (F-29);
   * signature: the job needs the customer's signature accepting the work (BL-052), which blocks
   * until they sign or the office says why it's missing; signedTotal: signed for one total, the
   * invoice now another, a heads-up; signatureNote: signed before the bill-to's name reached the iPad
   */
  key:
    | 'visits'
    | 'forms'
    | 'materials'
    | 'flags'
    | 'photos'
    | 'overpaid'
    | 'overrides'
    | 'po'
    | 'signature'
    | 'signedTotal'
    | 'signatureNote';
  ok: boolean;
  blocking: boolean;
  message: string;
}

export interface ReviewMaterial {
  id: string;
  name: string;
  quantity: string;
  unitPriceCents: number;
  totalCents: number;
  staffId: string | null;
  billProposed: boolean;
  decision: 'bill' | 'no_bill' | null;
  /** How much of it the invoice carries already, e.g. on the sold option's line (QA-797). */
  onInvoiceQuantity?: string;
}

export interface ReviewFlag {
  id: string;
  reason: string;
  detail: unknown;
  createdAt: string;
  /** What it's about: a photo's flag names the photo, to hide it from here. */
  entityType: string;
  entityId: string;
}

export interface Checklist {
  jobId: string;
  status: string;
  rowVersion: number;
  items: ChecklistItem[];
  canComplete: boolean;
  splits: { staffId: string; pct: string }[];
  splitsSource: 'auto' | 'manual';
  materials: ReviewMaterial[];
  flags: ReviewFlag[];
}

export interface InvoiceSummary {
  id: string;
  number: string;
  status: 'draft' | 'finalized' | 'sent' | 'partially_paid' | 'paid' | 'void';
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  balanceCents: number;
  lines: { id: string; description: string; quantity: string; unitPriceCents: number; totalCents: number }[];
  taxes: { code: string; rate: string; baseCents: number; taxCents: number }[];
  /** The customer's purchase order (F-29). */
  poNumber?: string | null;
}

/**
 * What ServiceTitan's record of a job says happened to it (jobs.get, history fix 2): an event
 * ("Job Booked: Booked by phone") with when, or the job's hold or cancel reason.
 */
export interface ImportedEvent {
  kind: 'history' | 'cancel_reason' | 'hold_reason';
  text: string;
  at: string;
}

/** One person's time on a job, in minutes; `running` while a clock is still going. */
export interface TimeLogged {
  staffId: string;
  name: string;
  workMinutes: number;
  driveMinutes: number;
  running: boolean;
}

export interface JobDetail {
  id: string;
  number: string;
  status: 'open' | 'in_progress' | 'awaiting_review' | 'completed' | 'cancelled';
  summary: string;
  customerId: string;
  customerName: string;
  /** The job's address (jobs.get): its site contact, when it has one, hears about its visits. */
  locationId?: string;
  address: string;
  /** The address's point on the map and where it came from (jobs.get, BL-018); null: none yet. */
  point?: AddressPointView | null;
  /** Who hears about the address's visits instead of the customer (F-54), with a phone to call. */
  siteContact?: { contactId: string; name: string; phone: string | null } | null;
  /** The business unit's name (jobs.get, history fix 1); none for a job with no department (QA-720). */
  businessUnitName?: string | null;
  /** A job from ServiceTitan: its priority and project, from ServiceTitan's own record. */
  fromServiceTitan?: { priority: string | null; projectId: string | null } | null;
  /** Who ServiceTitan says sold the job (jobs.get, history fix 2). */
  soldBy?: string | null;
  appointments: {
    id: string;
    status: string;
    start: string;
    end: string;
    techIds: string[];
    leadTechId: string | null;
    /** Its technicians by name (jobs.get). */
    techs?: { id: string; name: string }[];
    /** BL-027: the licences nobody on this visit holds (jobs.get). */
    licenceGaps?: LicenceGap[];
    /** BL-016: the customer replied C to the reminder for the visit's time. */
    confirmation?: VisitConfirmation | null;
    /** Why a cancelled visit was (jobs.get, history fix 2). */
    cancelReason?: string | null;
  }[];
  invoice: InvoiceSummary | null;
  /** The customer's signatures accepting the work on its invoices, oldest first (jobs.get, QA-727). */
  signatures?: AcceptanceSignature[];
  /** The time each person logged on it, working and driving (jobs.get, history fix 1). */
  timeLogged?: TimeLogged[];
  /** ServiceTitan's history of the job, oldest first (jobs.get, history fix 2); empty on our own. */
  importedHistory?: ImportedEvent[];
  /** The bill-to needs a PO on this job (F-29), when the server says. */
  poRequired?: boolean;
  /** The campaign the job is credited to (BL-008). */
  campaign?: CampaignRef | null;
  /** The memberships at the job's address (jobs.get, M8): a line from the book is member priced by them. */
  memberPlans?: MemberPlan[];
  /** Its notes, the techs' from the iPad and the office's, oldest first (jobs.get, QA-690). */
  notes?: JobNote[];
}

/** A note on a job: who wrote it and when (jobs.get, QA-690). */
export interface JobNote {
  id: string;
  body: string;
  at: string;
  author: string;
  appointmentId: string | null;
}

export interface Attachment {
  id: string;
  category: string;
  caption: string | null;
  kind: string;
  contentType: string;
  status: string;
  capturedAt: string | null;
  /** The marked-up picture once uploaded (BL-025), else its display copy once made, else the original. */
  url: string | null;
  /** Marked up, or shown as its display copy: the original, one tap away. */
  originalUrl: string | null;
  /** A picture's thumbnail, for lists. */
  thumbUrl?: string | null;
  annotated: boolean;
  /** How it shows (photo-display-model.ts). */
  display?: Display;
  /** customer: on the portal; internal: the office's only (attachments.setVisibility). */
  visibility: string;
  /** Who added it, by name. */
  addedBy: string | null;
  /** A video on its way up in parts (BL-082); null otherwise. */
  upload?: VideoUpload | null;
  /** Why a video failed its check (status 'failed'). */
  failedReason?: string | null;
  /** Pending again because what arrived wasn't the file (#73): "Upload it again". */
  checkFailed?: boolean;
}

/** A form question as the form's version defines it. */
export interface FormQuestion {
  key: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'choice' | 'date';
}

/** forms.forJob: the forms a job needs, and what the techs filled in. */
export interface JobForms {
  required: { definitionId: string; key: string; name: string; done: boolean }[];
  submissions: {
    id: string;
    definitionId: string;
    answers: Record<string, unknown>;
    complete: boolean;
    missing: string[];
    submittedAt: string;
    /** Who filled it in, by name (history fix 2); null when not known. */
    submittedBy?: string | null;
    /** The form's name, and the questions of the version it was filled on. */
    name: string;
    fields: FormQuestion[];
  }[];
}

/**
 * A submitted form as the office reads it (gap #4): its name, then each question in the form's order
 * with its answer in words ("yes", "Oct 15, 2026", "Not answered"), read only. An answer its form's
 * version has no question for (one brought from ServiceTitan whose form was read before any of its
 * answers were: history fix 4, 22 Oct) follows, by the label ServiceTitan's answer carries beside it
 * (`<key>:unit`), never shown itself.
 */
export function formAnswers(sub: JobForms['submissions'][number]): {
  name: string;
  rows: { label: string; answer: string }[];
} {
  const said = (q: Pick<FormQuestion, 'type'>, v: unknown): string => {
    if (v === undefined || v === null || (typeof v === 'string' && !v.trim())) return 'Not answered';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (q.type === 'date' && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v))
      return dateText(v, 'UTC');
    return String(v);
  };
  const asked = new Set(sub.fields.map((q) => q.key));
  const labelOf = (key: string): string => {
    const unit = sub.answers[`${key}:unit`] as { name?: unknown } | null | undefined;
    const name = typeof unit?.name === 'string' ? unit.name.trim() : '';
    return name || 'Question';
  };
  const unasked = Object.keys(sub.answers)
    .filter((k) => !k.endsWith(':unit') && !asked.has(k))
    .map((k) => ({ label: labelOf(k), answer: said({ type: 'text' }, sub.answers[k]) }));
  return {
    name: sub.name,
    rows: [...sub.fields.map((q) => ({ label: q.label, answer: said(q, sub.answers[q.key]) })), ...unasked],
  };
}

export interface StaffName {
  id: string;
  displayName: string;
  firstName: string;
  initials: string;
  color: string | null;
}

export function money(cents: number, currency = 'CAD'): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(cents / 100);
}

/**
 * The invoice card's state in words (QA-333): a draft the customer has already paid says so, as
 * "Draft, paid", and one partly paid says what is owing; otherwise its status.
 */
export function invoiceState(inv: Pick<InvoiceSummary, 'status' | 'totalCents' | 'balanceCents'>): string {
  if (inv.status === 'draft' && inv.totalCents > 0 && inv.balanceCents <= 0) return 'Draft, paid';
  if (inv.status === 'draft' && inv.balanceCents < inv.totalCents)
    return `Draft, ${money(inv.balanceCents)} owing`;
  return {
    draft: 'Draft',
    finalized: 'Finalized',
    sent: 'Sent',
    partially_paid: 'Partly paid',
    paid: 'Paid',
    void: 'Void',
  }[inv.status];
}

/** A job's photos, not its signature (QA-333): what the Photos tab counts and the checklist means. */
export const isPhoto = (a: Pick<Attachment, 'kind' | 'category'>): boolean =>
  a.kind === 'photo' && a.category !== 'signature';

/** "3 of 5 clear", and what still blocks completion (warnings never block). */
export function checklistSummary(items: readonly ChecklistItem[]): {
  clear: number;
  total: number;
  blockers: ChecklistItem[];
} {
  return {
    clear: items.filter((i) => i.ok).length,
    total: items.length,
    blockers: items.filter((i) => !i.ok && i.blocking),
  };
}

export type ItemLevel = 'ok' | 'bad' | 'warn';
export const itemLevel = (i: ChecklistItem): ItemLevel => (i.ok ? 'ok' : i.blocking ? 'bad' : 'warn');
export const LEVEL_LABEL: Record<ItemLevel, string> = { ok: 'Done', bad: 'Needs fixing', warn: 'Heads up' };

/** A queue card's chip: the first thing to fix, or ready. A job sent back goes to the tech (in
 * progress) and returns here when they pause it again; it says so. */
export function queueChip(j: QueueJob): { text: string; tone: 'ok' | 'warn' } {
  const back = j.sentBack ? 'Back from the tech: ' : '';
  if (!j.blocking.length) return { text: `${back}Ready to complete`, tone: 'ok' };
  return { text: `${back}${j.blocking.length} to fix: ${j.blocking[0]!.replace(/\.$/, '')}`, tone: 'warn' };
}

/** reviewFlags.list: what the office should look at, across jobs. */
export { flagText } from '@flowology/domain';

/** reviewFlags.list's answer: a page of flags, how many there are, and the id the next page starts after. */
export interface FlagPage {
  flags: OfficeFlag[];
  total: number;
  next: string | null;
}

export interface OfficeFlag {
  id: string;
  entityType: string;
  entityId: string;
  reason: string;
  createdAt: string;
  /** The job the flagged thing is on; null when it is on none. */
  jobId: string | null;
  jobNumber: string | null;
  customerName: string | null;
}

/** Splits are percentages with up to two decimals that must add up to exactly 100 (§6.4). */
export function splitsProblem(splits: readonly { staffId: string; pct: string }[]): string | null {
  if (!splits.length) return 'Someone has to have the credit.';
  let hundredths = 0;
  for (const s of splits) {
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(s.pct.trim())) return 'Each share is a percentage, like 50 or 33.33.';
    const [whole, frac = ''] = s.pct.trim().split('.');
    const v = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
    if (v <= 0) return 'Each share must be more than 0 %.';
    hundredths += v;
  }
  return hundredths === 10_000 ? null : `The shares add up to ${(hundredths / 100).toFixed(2)} %, not 100 %.`;
}

/**
 * What this person may do in Review, by the permission each action asks, whichever role gives it
 * (QA-681; the server checks again: these only decide which buttons show). While the company mirrors
 * ServiceTitan (`mirroring`), the job,
 * its visits, its estimates' prices and its invoice are ServiceTitan's (st-mirror §2): completing,
 * sending back, billing materials, another visit, OK'ing prices, reopening, voiding, the lines and
 * the PO are off, and the screen says so. Flags, the splits and a signature's waiver stay ours.
 */
export function reviewRights(held: readonly string[], mirroring = false) {
  const has = (...p: string[]) => p.every((x) => held.includes(x));
  const ours = !mirroring;
  return {
    /** The queue's flags and the job's splits: Flowology's own (reviewFlags.resolve, jobs.setSplits: jobs.complete). */
    review: has('jobs.complete'),
    /** Complete, send back, bill a material, book another visit, OK an estimate's prices (jobs.complete). */
    close: ours && has('jobs.complete'),
    /** What signed-out iPads sent: applied or discarded (sync.quarantine.manage). */
    quarantine: has('sync.quarantine.manage'),
    reopen: ours && has('jobs.reopen'),
    void: ours && has('invoices.void'),
    /** A draft's lines: added and taken off (invoices.lines), changed (invoices.edit); gap #1. */
    lines: ours && has('invoices.lines', 'invoices.edit'),
    /** Why a signature is missing (invoices.waiveAcknowledgement: invoices.edit, BL-052): Flowology's own columns. */
    waive: has('invoices.edit'),
    /** The customer's PO on the invoice (invoices.po.set, QA-514): a CSR too, though lines stay above. */
    po: ours && has('invoices.po.set'),
    /** ServiceTitan owns the job while the company mirrors it: say so where the controls were. */
    mirroring,
  };
}

// ── Timeline (jobs.timeline) ──

export const TIMELINE_KINDS = [
  ['comms', 'Calls & texts'],
  ['office', 'Booking & dispatch'],
  ['time', 'Drive & work'],
  ['photo', 'Photos'],
  ['note', 'Notes'],
  ['estimate', 'Estimates'],
  ['material', 'Materials'],
  ['form', 'Forms'],
  ['payment', 'Payment & invoice'],
  ['pause', 'Pauses'],
] as const;
export type TimelineKind = (typeof TIMELINE_KINDS)[number][0];

export interface TimelineEvent {
  kind: TimelineKind;
  at: string;
  title: string;
  detail: string | null;
  who: string | null;
  entityType: string;
  entityId: string;
  /** The money on it, in cents: an invoice's total when finalized, a payment's amount on it. */
  amountCents?: number | null;
}

/** One word for an event's kind, on its stone. */
export const KIND_WORD: Record<TimelineKind, string> = {
  comms: 'Call or text',
  office: 'Office',
  time: 'Time',
  photo: 'Photo',
  note: 'Note',
  estimate: 'Estimate',
  material: 'Material',
  form: 'Form',
  payment: 'Money',
  pause: 'Pause',
};

/** The water between two stones on the same day: what the gap was, when it was long enough to say. */
export function gapText(prev: TimelineEvent, next: TimelineEvent): string | null {
  const m = Math.round((Date.parse(next.at) - Date.parse(prev.at)) / 60_000);
  if (m < 15) return null;
  const long = m >= 60 ? `about ${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
  if (next.kind === 'time' && next.title === 'Arrived') return `Driving, ${long}`;
  if (prev.title === 'Arrived' || ['photo', 'note', 'material', 'form'].includes(prev.kind))
    return `Working, ${long}`;
  return `Quiet for ${long}`;
}

/** When a job was sent back, as the card says it: "today, 9:41 a.m.", "yesterday, …" or "Oct 14, …" (QA-288). */
export function sentBackWhen(at: string, timeZone: string, now = new Date()): string {
  const day = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'short' }).format(d);
  const time = new Intl.DateTimeFormat('en-CA', { timeZone, hour: 'numeric', minute: '2-digit' }).format(
    new Date(at),
  );
  const d = day(new Date(at));
  if (d === day(now)) return `today, ${time}`;
  if (d === day(new Date(now.getTime() - 86_400_000))) return `yesterday, ${time}`;
  const date = new Intl.DateTimeFormat('en-CA', { timeZone, month: 'short', day: 'numeric' }).format(
    new Date(at),
  );
  return `${date}, ${time}`;
}

/** Which jobs a live change touches enough to re-read the screen. */
export const REVIEW_EVENTS = /^(job|appointment|material|review_flag|invoice|payment|form)\./;

/**
 * What the office can do with an invoice (QA-341): a PDF of any invoice; Send and Copy link once
 * it is finalised (a draft still changes; a void one is gone).
 */
export function invoiceShare(status: InvoiceSummary['status']): {
  pdf: true;
  share: boolean;
  why: string | null;
} {
  if (status === 'void') return { pdf: true, share: false, why: 'A void invoice can’t be sent.' };
  if (status === 'draft')
    return { pdf: true, share: false, why: 'It can be sent once it’s finalized, when the job is completed.' };
  return { pdf: true, share: true, why: null };
}

/** The line after Send: where it went. */
export const sentText = (number: string, channel: 'sms' | 'email') =>
  `Invoice ${number} is on its way to the customer by ${channel === 'sms' ? 'text' : 'email'}.`;
